import React, { useState, useEffect, useCallback } from 'react';
// import { collection, query, where, getDocs, Timestamp, doc, getDoc, orderBy } from 'firebase-shim';
// import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
// import { Timestamp } from 'firebase-shim'; // Removed

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider,
  TablePagination,
  OutlinedInput,
  InputLabel as MuiInputLabel,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  ReceiptLong,
  PointOfSale,
  TrendingUp,
  Money,
  Clear as ClearIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Download as DownloadIcon,
  FilterAlt as FilterAltIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { formatCurrency, formatDate } from '../utils/formatters';
import { formatGSTN } from '../utils/gstUtils';
import AnimatedPage from '../components/AnimatedPage';
import { useNavigate } from 'react-router-dom';
import { generateInvoicePDF } from '../utils/invoicePdfGenerator';
import * as indexedDBService from '../utils/indexedDBService';
import SettingsService from '../services/settingsService';

interface Invoice {
  id: string;
  invoiceNumber: string;
  date: Date;
  clientId: string;
  clientName: string;
  clientDetails?: {
    gstin?: string;
    phone?: string;
    address?: string;
  };
  items: Array<{
    productId: string;
    productName: string;
    hsn: string;
    quantity: number;
    rate: number;
    amount: number;
    gstRate: number;
  }>;
  totalAmount: number;
  gstType: string;
  paymentType: string;
  paymentStatus: string;
  dueAmount: number;
  status: string;
  amount: number;
}

interface FilterOptions {
  startDate: Date | null;
  endDate: Date | null;
  party: string | null;
  hsn: string | null;
  product: string | null;
  gstType: string | null;
  paymentType: string | null;
  paymentStatus: string | null;
}

interface SummaryStats {
  totalSales: number;
  monthlySales: number;
  weeklySales: number;
  totalDues: number;
}

const Sales: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showCards, setShowCards] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [viewInvoiceDialogOpen, setViewInvoiceDialogOpen] = useState(false);
  const [fullPageTableOpen, setFullPageTableOpen] = useState(false);

  // Column visibility and filtering states
  const [columnVisibility, setColumnVisibility] = useState({
    date: true,
    invoiceNumber: true,
    gstNumber: true,
    clientName: true,
    taxableValue: true,
    taxAmount: true,
    invoiceValue: true,
    paymentReceived: true,
    balanceAmount: true,
    actions: true
  });
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    column: string;
  } | null>(null);
  const [columnFilters, setColumnFilters] = useState<{ [key: string]: string }>({});
  const [tableSearchQuery, setTableSearchQuery] = useState('');

  // Column definitions
  const columns = [
    { key: 'date', label: 'Date', width: 100 },
    { key: 'invoiceNumber', label: 'Invoice Number', width: 120 },
    { key: 'gstNumber', label: 'GST Number', width: 120 },
    { key: 'clientName', label: 'Client Name', width: 150 },
    { key: 'taxableValue', label: 'Taxable Value', width: 120 },
    { key: 'taxAmount', label: 'Tax Amount', width: 120 },
    { key: 'invoiceValue', label: 'Invoice Value', width: 120 },
    { key: 'paymentReceived', label: 'Payment Received', width: 120 },
    { key: 'balanceAmount', label: 'Balance Amount due', width: 150 },
    { key: 'actions', label: 'Actions', width: 100 }
  ];

  const [businessDetails, setBusinessDetails] = useState<any>(null);
  const [summaryStats, setSummaryStats] = useState<SummaryStats>({
    totalSales: 0,
    monthlySales: 0,
    weeklySales: 0,
    totalDues: 0
  });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    startDate: null,
    endDate: null,
    party: null,
    hsn: null,
    product: null,
    gstType: null,
    paymentType: null,
    paymentStatus: null
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportAllData, setExportAllData] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchInvoices = useCallback(async () => {
    if (!currentUser) {
      console.error('No user logged in');
      setError('Please log in to view invoices');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch user data for financial year settings
      const userProfile = await indexedDBService.getUserProfile(currentUser.uid);

      // Default financial year start date
      let startDate = new Date();
      startDate.setMonth(3); // April
      startDate.setDate(1);

      if (userProfile && userProfile.accountSettings?.financialYearStart) {
        startDate = new Date(userProfile.accountSettings.financialYearStart);
      }

      const invoicesList: Invoice[] = await indexedDBService.getAllItems<Invoice>('invoices');
      const clientsList = await indexedDBService.getAllItems<any>('clients');
      const clientsMap = new Map(clientsList.map(c => [c.id, c]));

      // Helper for robust date parsing
      const parseDate = (dateVal: any): Date => {
        if (!dateVal) return new Date();
        if (dateVal instanceof Date) return dateVal;
        if (typeof dateVal === 'string') return new Date(dateVal);
        if (typeof dateVal === 'number') return new Date(dateVal);
        // Handle Firebase Timestamp-like objects { seconds, nanoseconds }
        if (dateVal.seconds) return new Date(dateVal.seconds * 1000);
        return new Date();
      };

      const processedInvoices = invoicesList
        .filter(inv => inv.status !== 'deleted' && inv.paymentStatus !== 'deleted')
        .map(invoiceData => {
          const invoiceDate = parseDate(invoiceData.date);

          // Fetch client details
          let clientDetails;
          let clientName = invoiceData.clientName || '';

          if (invoiceData.clientId && clientsMap.has(invoiceData.clientId)) {
            const clientData = clientsMap.get(invoiceData.clientId);
            clientName = clientData.name || clientName;
            clientDetails = {
              gstin: clientData.gstin || '',
              phone: clientData.phone || '',
              address: clientData.address || ''
            };
          }

          // Calculate total amount
          const totalAmount = (invoiceData.items || []).reduce((sum: number, item: any) => {
            return sum + (Number(item.amount) || 0);
          }, 0);

          const gstType = clientDetails?.gstin ? 'B2B' : 'B2C';

          let normalizedStatus = invoiceData.status || invoiceData.paymentStatus || 'pending';

          let dueAmount = Number(invoiceData.dueAmount) || 0;
          if (normalizedStatus.toLowerCase() !== 'paid' && dueAmount === 0) {
            dueAmount = totalAmount;
          }

          return {
            ...invoiceData,
            id: invoiceData.id,
            invoiceNumber: invoiceData.invoiceNumber || '',
            date: invoiceDate,
            clientId: invoiceData.clientId || '',
            clientName: clientName,
            clientDetails,
            items: (invoiceData.items || []).map(item => ({
              ...item,
              productId: item.productId || '',
              productName: item.productName || '',
              hsn: item.hsn || '',
              // Ensure numeric values
              quantity: Number(item.quantity) || 0,
              rate: Number(item.rate) || 0,
              amount: Number(item.amount) || 0,
              gstRate: Number(item.gstRate) || 0
            })),
            totalAmount: totalAmount,
            gstType: gstType,
            paymentType: invoiceData.paymentType || '',
            paymentStatus: normalizedStatus,
            dueAmount: dueAmount,
            status: normalizedStatus,
            amount: totalAmount
          };
        });

      // Sort invoices by date (newest first)
      const sortedInvoices = processedInvoices.sort((a, b) => {
        return b.date.getTime() - a.date.getTime();
      });

      setInvoices(sortedInvoices);
      calculateSummaries(sortedInvoices);
    } catch (err) {
      console.error('Error details:', err);
      setError('Failed to fetch invoices. Please try again.');
      setSnackbar({
        open: true,
        message: 'Error fetching invoices: ' + (err instanceof Error ? err.message : 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const calculateSummaries = (invoiceData: Invoice[]) => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));

    const stats: SummaryStats = {
      totalSales: 0,
      monthlySales: 0,
      weeklySales: 0,
      totalDues: 0
    };

    invoiceData.forEach(invoice => {
      const invoiceDate = invoice.date;

      stats.totalSales += invoice.totalAmount;

      if (invoiceDate >= monthStart) {
        stats.monthlySales += invoice.totalAmount;
      }
      if (invoiceDate >= weekStart) {
        stats.weeklySales += invoice.totalAmount;
      }
      // Calculate total dues (receivables) from unpaid invoices
      if ((invoice.paymentStatus || '').toLowerCase() !== 'paid') {
        // If dueAmount is not explicitly set, use the total amount as the due amount
        const actualDueAmount = invoice.dueAmount > 0 ? invoice.dueAmount : invoice.totalAmount;
        stats.totalDues += actualDueAmount;
      }
    });

    setSummaryStats(stats);
  };

  const filterInvoices = (invoices: Invoice[]) => {
    return invoices.filter(invoice => {
      // Date filter
      if (filterOptions.startDate && invoice.date < filterOptions.startDate) return false;
      if (filterOptions.endDate && invoice.date > filterOptions.endDate) return false;

      // Party filter
      if (filterOptions.party && invoice.clientName &&
        !invoice.clientName.toLowerCase().includes(filterOptions.party.toLowerCase())) return false;

      // HSN filter
      if (filterOptions.hsn && !invoice.items.some(item => item.hsn === filterOptions.hsn)) return false;

      // Product filter
      if (filterOptions.product && !invoice.items.some(item =>
        item.productName && item.productName.toLowerCase().includes(filterOptions.product!.toLowerCase())
      )) return false;

      // GST type filter
      if (filterOptions.gstType && invoice.gstType !== filterOptions.gstType) return false;

      // Payment type filter
      if (filterOptions.paymentType && invoice.paymentType !== filterOptions.paymentType) return false;

      // Payment status filter
      if (filterOptions.paymentStatus && (invoice.paymentStatus || '').toLowerCase() !== filterOptions.paymentStatus.toLowerCase()) return false;

      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          (invoice.invoiceNumber && invoice.invoiceNumber.toLowerCase().includes(query)) ||
          (invoice.clientName && invoice.clientName.toLowerCase().includes(query)) ||
          invoice.items.some(item => item.productName && item.productName.toLowerCase().includes(query))
        );
      }

      // Table search query filter
      if (tableSearchQuery) {
        const query = tableSearchQuery.toLowerCase();
        const searchableFields = [
          invoice.invoiceNumber || '',
          invoice.clientName || '',
          invoice.clientDetails?.gstin || '',
          formatDate(invoice.date),
          (invoice.paymentStatus || 'Pending').toLowerCase()
        ];
        if (!searchableFields.some(field => field.toLowerCase().includes(query))) return false;
      }

      // Column filters
      for (const [column, filterValue] of Object.entries(columnFilters)) {
        if (!filterValue) continue;

        let cellValue = '';
        switch (column) {
          case 'date':
            cellValue = formatDate(invoice.date);
            break;
          case 'invoiceNumber':
            cellValue = invoice.invoiceNumber || '';
            break;
          case 'gstNumber':
            cellValue = invoice.clientDetails?.gstin || '';
            break;
          case 'clientName':
            cellValue = invoice.clientName || '';
            break;
          case 'paymentReceived':
            // Calculate payment received based on status
            const paymentStatusLower = invoice.paymentStatus ? invoice.paymentStatus.toLowerCase() : '';
            const paymentReceived = paymentStatusLower === 'paid' ? invoice.totalAmount : 0;
            cellValue = formatCurrency(paymentReceived);
            break;
          case 'balanceAmount':
            // Calculate balance amount
            const balanceStatusLower = invoice.paymentStatus ? invoice.paymentStatus.toLowerCase() : '';
            const balanceDue = balanceStatusLower === 'paid' ? 0 : (invoice.dueAmount > 0 ? invoice.dueAmount : invoice.totalAmount);
            cellValue = formatCurrency(balanceDue);
            break;
        }

        if (!cellValue.toLowerCase().includes(filterValue.toLowerCase())) return false;
      }

      return true;
    });
  };

  const exportToCSV = () => {
    const dataToExport = exportAllData ? invoices : filterInvoices(invoices);
    if (dataToExport.length === 0) return;

    const headers = [
      'Date',
      'Invoice No',
      'Client',
      'GSTIN',
      'Product',
      'HSN',
      'Quantity',
      'Rate',
      'Amount',
      'GST Type',
      'Payment Type',
      'Status',
      'Due Amount'
    ];

    const csvRows = [
      headers.join(','),
      ...dataToExport.flatMap(invoice =>
        invoice.items.map(item => [
          formatDate(invoice.date),
          invoice.invoiceNumber,
          invoice.clientName,
          invoice.clientDetails?.gstin ? formatGSTN(invoice.clientDetails.gstin) : 'B2C',
          item.productName,
          item.hsn,
          item.quantity,
          item.rate,
          item.amount,
          invoice.gstType,
          invoice.paymentType,
          invoice.paymentStatus,
          invoice.dueAmount
        ].join(','))
      )
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `sales_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setExportDialogOpen(false);
  };

  const clearFilters = () => {
    setFilterOptions({
      startDate: null,
      endDate: null,
      party: null,
      hsn: null,
      product: null,
      gstType: null,
      paymentType: null,
      paymentStatus: null
    });
    setSearchQuery('');

    // Show a confirmation message
    setSnackbar({
      open: true,
      message: 'Filters have been cleared',
      severity: 'success'
    });
  };

  // Column management functions
  const handleColumnContextMenu = (event: React.MouseEvent, column: string) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      column
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const toggleColumnVisibility = (column: string) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column as keyof typeof prev]
    }));
    handleCloseContextMenu();
  };

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const clearColumnFilters = () => {
    setColumnFilters({});
    setTableSearchQuery('');
  };

  const getColumnFilterOptions = (column: string, invoices: Invoice[]) => {
    const values = invoices.map(invoice => {
      switch (column) {
        case 'date':
          return formatDate(invoice.date);
        case 'invoiceNumber':
          return invoice.invoiceNumber || '';
        case 'gstNumber':
          return invoice.clientDetails?.gstin || '';
        case 'clientName':
          return invoice.clientName || '';
        case 'paymentReceived':
          const paymentStatusLower = invoice.paymentStatus ? invoice.paymentStatus.toLowerCase() : '';
          const paymentReceived = paymentStatusLower === 'paid' ? invoice.totalAmount : 0;
          return formatCurrency(paymentReceived);
        case 'balanceAmount':
          const balanceStatusLower = invoice.paymentStatus ? invoice.paymentStatus.toLowerCase() : '';
          const balanceDue = balanceStatusLower === 'paid' ? 0 : (invoice.dueAmount > 0 ? invoice.dueAmount : invoice.totalAmount);
          return formatCurrency(balanceDue);
        default:
          return '';
      }
    });
    return Array.from(new Set(values)).filter(Boolean).sort();
  };

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Fetch the business details
  useEffect(() => {
    const fetchBusinessDetails = async () => {
      if (!currentUser) return;

      try {
        const businesses = await indexedDBService.getAllItems<any>('businesses');
        if (businesses.length > 0) {
          setBusinessDetails(businesses[0]);
        }
      } catch (error) {
        console.error("Error fetching business details:", error);
      }
    };

    fetchBusinessDetails();
  }, [currentUser]);

  // Handle view invoice
  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setViewInvoiceDialogOpen(true);
  };

  // Handle close view invoice
  const handleCloseViewInvoice = () => {
    setViewInvoiceDialogOpen(false);
  };

  // Handle edit invoice
  const handleEditInvoice = (invoiceId: string) => {
    navigate(`/invoices/edit/${invoiceId}`);
  };

  // Function to print the invoice
  const handlePrintInvoice = async () => {
    if (!selectedInvoice) return;

    // Fetch the full business details to ensure bank info and QR codes are included
    let printBusinessDetails = { ...businessDetails };
    if (currentUser) {
      try {
        const userSettings = await SettingsService.fetchSettings(currentUser.uid);
        if (userSettings && userSettings.businessProfile) {
          printBusinessDetails = {
            ...printBusinessDetails,
            ...userSettings.businessProfile
          };
        }
      } catch (e) {
        console.error('Error fetching business profile for printing:', e);
      }
    }

    // Create a temporary div to hold the invoice content
    const printContent = document.createElement('div');
    printContent.style.width = '210mm'; // A4 width
    printContent.style.padding = '20mm'; // 2cm margin
    printContent.style.fontFamily = 'Arial, sans-serif';
    printContent.style.fontSize = '12pt';

    // Calculate tax amount
    const taxableValue = selectedInvoice.items.reduce((sum, item) => {
      return sum + (Number(item.amount) || 0);
    }, 0);

    const taxAmount = selectedInvoice.items.reduce((sum, item) => {
      const gstRate = Number(item.gstRate) || 0;
      const itemValue = Number(item.amount) || 0;
      const itemTax = itemValue * (gstRate / 100);
      return sum + parseFloat(itemTax.toFixed(2));
    }, 0);

    const printBankName = printBusinessDetails?.bankName || printBusinessDetails?.bankDetails?.bankName || '';
    const printBankBranch = printBusinessDetails?.bankBranch || printBusinessDetails?.bankDetails?.branchName || '';
    const printAccountNumber = printBusinessDetails?.accountNumber || printBusinessDetails?.bankDetails?.accountNumber || '';
    const printIfscCode = printBusinessDetails?.ifscCode || printBusinessDetails?.ifsc || printBusinessDetails?.bankDetails?.ifscCode || '';
    const printUpiId = printBusinessDetails?.upiId || (printBusinessDetails as any)?.bankDetails?.upiId || '';
    const printUpiQrImage = printBusinessDetails?.upiQrImage || printBusinessDetails?.qrCode || (printUpiId ? `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`upi://pay?pa=${printUpiId}&pn=${encodeURIComponent(printBusinessDetails?.name || printBusinessDetails?.businessName || 'Business')}&am=${selectedInvoice.amount || 0}&cu=INR`)}` : '');

    // Add the invoice content - properly formatted for printing
    printContent.innerHTML = `
      <style>
        @media print {
          body { margin: 0; padding: 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; margin-bottom: 20px; }
          th { background-color: #f3f3f3; text-align: left; padding: 8px; border-bottom: 1px solid #ddd; }
          td { padding: 8px; border-bottom: 1px solid #ddd; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .invoice-title { font-size: 24pt; font-weight: bold; color: #2196F3; margin-bottom: 10px; }
          .section-title { font-size: 14pt; font-weight: bold; margin-top: 20px; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
          .summary { margin-top: 20px; text-align: right; }
          .summary-table { width: 250px; margin-left: auto; }
          .summary-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .total-row { font-weight: bold; border-top: 1px solid #ddd; padding-top: 5px; margin-top: 5px; }
          .terms { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; font-size: 10pt; }
          .footer { margin-top: 50px; font-size: 10pt; text-align: center; color: #666; }
        }
      </style>
      <div class="header">
        <div>
          <div class="invoice-title">INVOICE</div>
          <div style="font-size: 14pt; color: #666;">#${selectedInvoice.invoiceNumber}</div>
        </div>
        <div style="text-align: right;">
          <div style="font-weight: bold;">${printBusinessDetails?.name || 'Business Name'}</div>
          <div>${printBusinessDetails?.address || ''}</div>
          <div>${printBusinessDetails?.city || ''} ${printBusinessDetails?.pincode || ''}</div>
          <div>${printBusinessDetails?.phone ? `Phone: ${printBusinessDetails.phone}` : ''}</div>
          <div>${printBusinessDetails?.email || ''}</div>
          <div>${printBusinessDetails?.gstin ? `GSTIN: ${printBusinessDetails.gstin}` : ''}</div>
        </div>
      </div>

      <div style="display: flex; justify-content: space-between;">
        <div style="width: 45%;">
          <div class="section-title">Bill To</div>
          <div style="font-weight: bold;">${selectedInvoice.clientName}</div>
          <div>${selectedInvoice.clientDetails?.address || ''}</div>
          ${selectedInvoice.clientDetails?.gstin ? `<div>GSTIN: ${selectedInvoice.clientDetails.gstin}</div>` : ''}
          ${selectedInvoice.clientDetails?.phone ? `<div>Phone: ${selectedInvoice.clientDetails.phone}</div>` : ''}
        </div>
        <div style="width: 45%; text-align: right;">
          <div class="section-title">Invoice Details</div>
          <div style="display: flex; justify-content: space-between;"><span>Invoice Date:</span> <span>${formatDate(selectedInvoice.date)}</span></div>
          <div style="display: flex; justify-content: space-between;"><span>Status:</span> <span>${getPaymentStatusLabel(selectedInvoice.paymentStatus)}</span></div>
          <div style="display: flex; justify-content: space-between;"><span>Invoice Type:</span> <span>${selectedInvoice.gstType}</span></div>
        </div>
      </div>

      <div class="section-title">Items</div>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>HSN</th>
            <th style="text-align: right;">Qty</th>
            <th style="text-align: right;">Rate</th>
            <th style="text-align: right;">GST %</th>
            <th style="text-align: right;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${selectedInvoice.items.map(item => `
            <tr>
              <td>${item.productName}</td>
              <td>${item.hsn}</td>
              <td style="text-align: right;">${item.quantity}</td>
              <td style="text-align: right;">₹${item.rate.toFixed(2)}</td>
              <td style="text-align: right;">${item.gstRate}%</td>
              <td style="text-align: right;">₹${item.amount.toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="summary">
        <div class="summary-table">
          <div class="summary-row">
            <span>Taxable Value:</span>
            <span>₹${taxableValue.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span>Tax Amount:</span>
            <span>₹${taxAmount.toFixed(2)}</span>
          </div>
          <div class="summary-row total-row">
            <span>Total:</span>
            <span>₹${selectedInvoice.totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div class="terms">
        <p>Payment is due within 15 days from the date of invoice. Late payments may be subject to interest charges.</p>
        <p>All disputes are subject to local jurisdiction.</p>
      </div>

      ${(printBankName || printUpiId) ? `
        <div class="section-title">Payment Details</div>
        <div style="display: flex; gap: 20px; align-items: flex-start; margin-bottom: 20px;">
          <div style="flex: 1;">
            ${printBankName ? `<div><strong>Bank:</strong> ${printBankName}</div>` : ''}
            ${printBankBranch ? `<div><strong>Branch:</strong> ${printBankBranch}</div>` : ''}
            ${printAccountNumber ? `<div><strong>Account Number:</strong> ${printAccountNumber}</div>` : ''}
            ${printIfscCode ? `<div><strong>IFSC Code:</strong> ${printIfscCode}</div>` : ''}
            ${printUpiId ? `<div><strong>UPI ID:</strong> ${printUpiId}</div>` : ''}
          </div>
          ${printUpiQrImage ? `
            <div>
              <img src="${printUpiQrImage}" alt="UPI QR Code" style="max-width: 120px; max-height: 120px;" />
            </div>
          ` : ''}
        </div>
      ` : ''}

      <div class="footer">
        Thank you for your business!
      </div>
    `;

    const htmlContent = `
      <html>
        <head>
          <title>Invoice #${selectedInvoice.invoiceNumber}</title>
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
      </html>
    `;

    // Use a hidden iframe instead of a new window to prevent freezing the parent tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();

      // Wait for content to render before printing
      setTimeout(() => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
        // Clean up
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      }, 500);
    }
  };

  // Function for thermal printing
  const handleThermalPrint = () => {
    if (!selectedInvoice) return;

    // Create a temporary div to hold the thermal receipt content
    const printContent = document.createElement('div');
    printContent.style.width = '80mm'; // Standard thermal receipt width
    printContent.style.fontFamily = 'monospace';
    printContent.style.fontSize = '10pt';

    // Calculate tax amount
    const taxAmount = selectedInvoice.items.reduce((sum, item) => {
      const gstRate = Number(item.gstRate) || 0;
      const itemValue = Number(item.amount) || 0;
      const itemTax = itemValue * (gstRate / 100);
      return sum + parseFloat(itemTax.toFixed(2));
    }, 0);

    // Add the receipt content - formatted for thermal printer
    printContent.innerHTML = `
      <style>
        @media print {
          body { margin: 0; padding: 0; font-family: monospace; font-size: 10pt; }
          .center { text-align: center; }
          .divider { border-bottom: 1px dashed #000; margin: 5px 0; }
          .receipt-item { display: flex; justify-content: space-between; margin: 2px 0; }
          .column-70 { width: 70%; }
          .column-30 { width: 30%; text-align: right; }
        }
      </style>
      <div class="center">
        <div style="font-weight: bold; font-size: 12pt;">${businessDetails?.name || 'Business Name'}</div>
        <div>${businessDetails?.address || ''}</div>
        <div>${businessDetails?.city || ''} ${businessDetails?.pincode || ''}</div>
        <div>${businessDetails?.phone ? `Phone: ${businessDetails.phone}` : ''}</div>
        <div>${businessDetails?.gstin ? `GSTIN: ${businessDetails.gstin}` : ''}</div>
      </div>
      
      <div class="divider"></div>
      
      <div class="center">
        <div style="font-weight: bold;">INVOICE #${selectedInvoice.invoiceNumber}</div>
        <div>Date: ${formatDate(new Date(selectedInvoice.date))}</div>
      </div>
      
      <div class="divider"></div>
      
      <div>
        <div>Customer: ${selectedInvoice.clientName}</div>
        ${selectedInvoice.clientDetails?.phone ? `<div>Phone: ${selectedInvoice.clientDetails.phone}</div>` : ''}
        ${selectedInvoice.clientDetails?.gstin ? `<div>GSTIN: ${selectedInvoice.clientDetails.gstin}</div>` : ''}
      </div>
      
      <div class="divider"></div>
      
      <div style="font-weight: bold;" class="receipt-item">
        <span class="column-70">Item</span>
        <span class="column-30">Amount</span>
      </div>
      <div class="divider"></div>
      
      ${selectedInvoice.items.map(item => `
        <div class="receipt-item">
          <div class="column-70">
            ${item.productName}<br>
            ${item.quantity} x ₹${item.rate.toFixed(2)}
            ${item.gstRate > 0 ? `<br>GST ${item.gstRate}%` : ''}
          </div>
          <div class="column-30">₹${item.amount.toFixed(2)}</div>
        </div>
      `).join('<div class="divider"></div>')}
      
      <div class="divider"></div>
      
      <div class="receipt-item">
        <span class="column-70">Tax Amount:</span>
        <span class="column-30">₹${taxAmount.toFixed(2)}</span>
      </div>
      
      <div class="receipt-item" style="font-weight: bold; font-size: 12pt;">
        <span class="column-70">TOTAL:</span>
        <span class="column-30">₹${selectedInvoice.totalAmount.toFixed(2)}</span>
      </div>
      
      <div class="divider"></div>
      
      <div class="center" style="margin-top: 10px;">
        <div>Payment Status: ${getPaymentStatusLabel(selectedInvoice.paymentStatus)}</div>
        <div style="margin-top: 10px;">Thank you for your business!</div>
      </div>
    `;

    const htmlContent = `
      <html>
        <head>
          <title>Receipt #${selectedInvoice.invoiceNumber}</title>
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
      </html>
    `;

    // Use a hidden iframe instead of a new window to prevent freezing the parent tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();

      // Wait for content to render before printing
      setTimeout(() => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
        // Clean up
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      }, 500);
    }
  };

  // Function to save the invoice as PDF
  const handleSavePDF = async () => {
    if (!selectedInvoice) return;

    try {
      setLoading(true);

      // Convert the sales invoice format to the format expected by generateInvoicePDF
      const invoiceForPdf = {
        id: selectedInvoice.id,
        invoiceNumber: selectedInvoice.invoiceNumber,
        date: formatDate(new Date(selectedInvoice.date)),
        dueDate: '',
        client: selectedInvoice.clientName,
        clientDetails: {
          address: selectedInvoice.clientDetails?.address || '',
          gstin: selectedInvoice.clientDetails?.gstin || '',
          email: '',
          phone: selectedInvoice.clientDetails?.phone || '',
          state: ''
        },
        items: selectedInvoice.items.map(item => ({
          id: Math.random(),
          name: item.productName,
          description: '',
          quantity: item.quantity,
          rate: item.rate,
          amount: item.amount,
          hsnCode: item.hsn,
          gstRate: item.gstRate
        })),
        subtotal: selectedInvoice.totalAmount,
        tax: 0,
        discount: 0,
        amount: selectedInvoice.totalAmount,
        status: selectedInvoice.paymentStatus as any,
        notes: '',
        termsAndConditions: '',
        taxType: selectedInvoice.gstType,
        taxRate: 0,
        taxable: true,
        discountType: 'fixed',
        discountValue: 0,
        createdAt: new Date(selectedInvoice.date).toISOString(),
        userId: currentUser?.uid || ''
      };

      // Generate the PDF with the default template
      const pdfBytes = await generateInvoicePDF(
        invoiceForPdf,
        businessDetails || { name: 'Business' },
        1 // Default template ID
      );

      // Convert Uint8Array to Blob
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

      // Create a URL for the blob
      const url = URL.createObjectURL(pdfBlob);

      // Create a link element for download
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${selectedInvoice.invoiceNumber.replace(/[/\\:*?"<>|]/g, '_')}.pdf`;

      // Append to the document and click
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'PDF saved successfully',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate PDF. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to email the PDF
  const handleEmailPDF = async () => {
    if (!selectedInvoice) return;

    try {
      // Convert the sales invoice format to the format expected by generateInvoicePDF (same as in handleSavePDF)
      const invoiceForPdf = {
        id: selectedInvoice.id,
        invoiceNumber: selectedInvoice.invoiceNumber,
        date: formatDate(new Date(selectedInvoice.date)),
        dueDate: '',
        client: selectedInvoice.clientName,
        clientDetails: {
          address: selectedInvoice.clientDetails?.address || '',
          gstin: selectedInvoice.clientDetails?.gstin || '',
          email: '',
          phone: selectedInvoice.clientDetails?.phone || '',
          state: ''
        },
        items: selectedInvoice.items.map(item => ({
          id: Math.random(),
          name: item.productName,
          description: '',
          quantity: item.quantity,
          rate: item.rate,
          amount: item.amount,
          hsnCode: item.hsn,
          gstRate: item.gstRate
        })),
        subtotal: selectedInvoice.totalAmount,
        tax: 0,
        discount: 0,
        amount: selectedInvoice.totalAmount,
        status: selectedInvoice.paymentStatus as any,
        notes: '',
        termsAndConditions: '',
        taxType: selectedInvoice.gstType,
        taxRate: 0,
        taxable: true,
        discountType: 'fixed',
        discountValue: 0,
        createdAt: new Date(selectedInvoice.date).toISOString(),
        userId: currentUser?.uid || ''
      };

      // First get the PDF as a blob
      const pdfBytes = await generateInvoicePDF(
        invoiceForPdf,
        businessDetails || { name: 'Business' },
        1 // Default template ID
      );

      // Convert Uint8Array to Blob
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

      // Check if we have client email
      // If client details has phone but not email, we could use the phone for WhatsApp
      const clientContact = selectedInvoice.clientDetails?.phone || '';

      if (clientContact) {
        const subject = `Invoice ${selectedInvoice.invoiceNumber} from ${businessDetails?.name || 'Business'}`;
        const body = `Dear ${selectedInvoice.clientName},\n\nPlease find attached the invoice ${selectedInvoice.invoiceNumber} for your recent purchase.\n\nTotal Amount: ₹${selectedInvoice.totalAmount.toFixed(2)}\n\nThank you for your business.\n\nRegards,\n${businessDetails?.name || 'Business'}`;

        // For demo, just open mail client - in a real app, you would use a backend service
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);

        setSnackbar({
          open: true,
          message: 'Email client opened. Please attach the downloaded PDF.',
          severity: 'info'
        });

        // Also download the PDF since we can't attach it via mailto
        const link = document.createElement('a');
        link.href = URL.createObjectURL(pdfBlob);
        link.download = `Invoice_${selectedInvoice.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setSnackbar({
          open: true,
          message: 'Client contact not available',
          severity: 'warning'
        });

        // Still download the PDF
        const link = document.createElement('a');
        link.href = URL.createObjectURL(pdfBlob);
        link.download = `Invoice_${selectedInvoice.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error preparing email:', error);
      setSnackbar({
        open: true,
        message: 'Failed to prepare invoice for email',
        severity: 'error'
      });
    }
  };

  // Function to view the next invoice
  const handleViewNextInvoice = () => {
    if (!selectedInvoice || invoices.length <= 1) return;

    // Get the currently displayed invoices based on filters
    const filteredInvoices = filterInvoices(invoices);
    if (filteredInvoices.length <= 1) return;

    // Find the index of the current invoice
    const currentIndex = filteredInvoices.findIndex(inv => inv.id === selectedInvoice.id);

    // Get the next invoice, wrapping around to the first if at the end
    const nextIndex = (currentIndex + 1) % filteredInvoices.length;
    const nextInvoice = filteredInvoices[nextIndex];

    // Set as the selected invoice
    setSelectedInvoice(nextInvoice);
  };

  const renderSummaryCards = () => (
    <Collapse in={showCards}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 203, 243, 0.05) 100%)',
              border: '1px solid rgba(33, 150, 243, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(33, 150, 243, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(33, 150, 243, 0.2)',
                  color: '#2196F3',
                  mr: 2
                }}
              >
                <ReceiptLong fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Total Sales
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#2196F3' }}>
              {formatCurrency(summaryStats.totalSales)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(129, 199, 132, 0.05) 100%)',
              border: '1px solid rgba(76, 175, 80, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  color: '#4CAF50',
                  mr: 2
                }}
              >
                <PointOfSale fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Monthly Sales
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#4CAF50' }}>
              {formatCurrency(summaryStats.monthlySales)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 183, 77, 0.05) 100%)',
              border: '1px solid rgba(255, 152, 0, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(255, 152, 0, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  color: '#FF9800',
                  mr: 2
                }}
              >
                <TrendingUp fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Weekly Sales
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#FF9800' }}>
              {formatCurrency(summaryStats.weeklySales)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(229, 115, 115, 0.05) 100%)',
              border: '1px solid rgba(244, 67, 54, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(244, 67, 54, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  color: '#F44336',
                  mr: 2
                }}
              >
                <Money fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Total Receivables
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#F44336' }}>
              {formatCurrency(summaryStats.totalDues)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Collapse>
  );

  const renderFilters = () => (
    <Collapse in={showFilters}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Filter Invoices
          </Typography>
          <Button
            startIcon={<ClearIcon />}
            size="small"
            onClick={clearFilters}
            variant="outlined"
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Clear Filters
          </Button>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth variant="outlined">
              <MuiInputLabel htmlFor="search-field">Search</MuiInputLabel>
              <OutlinedInput
                id="search-field"
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {/* Trigger filter refresh */ }}
                      sx={{ minWidth: '80px' }}
                    >
                      Search
                    </Button>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth>
              <InputLabel>GST Type</InputLabel>
              <Select
                value={filterOptions.gstType || ''}
                onChange={(e) => setFilterOptions({ ...filterOptions, gstType: e.target.value })}
                label="GST Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="B2B">B2B</MenuItem>
                <MenuItem value="B2C">B2C</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth>
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={filterOptions.paymentType || ''}
                onChange={(e) => setFilterOptions({ ...filterOptions, paymentType: e.target.value })}
                label="Payment Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={filterOptions.paymentStatus || ''}
                onChange={(e) => setFilterOptions({ ...filterOptions, paymentStatus: e.target.value })}
                label="Payment Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    </Collapse>
  );

  const renderTable = (isFullPage = false) => {
    const filteredInvoices = filterInvoices(invoices);

    const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    // Apply pagination
    const displayedInvoices = rowsPerPage > 0
      ? filteredInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : filteredInvoices;

    return (
      <>
        {/* Table Search Bar */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search in table..."
            value={tableSearchQuery}
            onChange={(e) => setTableSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: tableSearchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setTableSearchQuery('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 300 }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearColumnFilters}
            disabled={Object.keys(columnFilters).length === 0 && !tableSearchQuery}
          >
            Clear Filters
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{
          borderRadius: isFullPage ? 0 : 2,
          boxShadow: isFullPage ? 'none' : '0 4px 20px 0 rgba(0,0,0,0.14)',
          overflowX: 'auto',
          width: '100%',
          maxHeight: isFullPage ? 'calc(100vh - 200px)' : 'none',
          height: isFullPage ? 'calc(100vh - 200px)' : 'auto'
        }}>
          <Table sx={{ minWidth: 1200 }} size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
                {columns.map((column) => {
                  if (!columnVisibility[column.key as keyof typeof columnVisibility]) return null;

                  return (
                    <TableCell
                      key={column.key}
                      sx={{
                        color: 'white',
                        whiteSpace: 'nowrap',
                        minWidth: `${column.width}px`,
                        fontWeight: 'bold',
                        position: 'relative',
                        cursor: 'context-menu'
                      }}
                      onContextMenu={(e) => handleColumnContextMenu(e, column.key)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{column.label}</span>
                        {columnFilters[column.key] && (
                          <Chip
                            size="small"
                            label={columnFilters[column.key]}
                            onDelete={() => handleColumnFilterChange(column.key, '')}
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              color: 'white'
                            }}
                          />
                        )}
                        <FilterAltIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>

              {/* Column Filter Row */}
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                {columns.map((column) => {
                  if (!columnVisibility[column.key as keyof typeof columnVisibility]) return null;

                  const filterOptions = getColumnFilterOptions(column.key, filteredInvoices);

                  return (
                    <TableCell key={`filter-${column.key}`} sx={{ padding: '8px', minWidth: `${column.width}px` }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={columnFilters[column.key] || ''}
                          onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                          displayEmpty
                          sx={{ fontSize: '0.8rem' }}
                        >
                          <MenuItem value="">
                            <em>All {column.label}</em>
                          </MenuItem>
                          {filterOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedInvoices.map(invoice => {
                // Calculate taxable value (sum of all items' amounts)
                const taxableValue = invoice.items.reduce((sum, item) => {
                  return sum + (Number(item.amount) || 0);
                }, 0);

                // Calculate tax amount (sum of all GST amounts)
                const taxAmount = invoice.items.reduce((sum, item) => {
                  const gstRate = Number(item.gstRate) || 0;
                  const itemValue = Number(item.amount) || 0;
                  const itemTax = itemValue * (gstRate / 100);
                  return sum + (itemTax && !isNaN(itemTax) ? parseFloat(itemTax.toFixed(2)) : 0);
                }, 0);

                // Calculate payment received
                const paymentStatusLower = invoice.paymentStatus ? invoice.paymentStatus.toLowerCase() : '';
                const paymentReceived = paymentStatusLower === 'paid'
                  ? invoice.totalAmount
                  : 0;

                // Calculate balance due
                const balanceDue = paymentStatusLower === 'paid'
                  ? 0
                  : (invoice.dueAmount > 0 ? invoice.dueAmount : invoice.totalAmount);

                return (
                  <TableRow key={invoice.id} hover>
                    {columnVisibility.date && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(new Date(invoice.date))}</TableCell>
                    )}
                    {columnVisibility.invoiceNumber && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.invoiceNumber}</TableCell>
                    )}
                    {columnVisibility.gstNumber && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.clientDetails?.gstin ? formatGSTN(invoice.clientDetails.gstin) : 'B2C'}</TableCell>
                    )}
                    {columnVisibility.clientName && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.clientName}</TableCell>
                    )}
                    {columnVisibility.taxableValue && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(taxableValue)}</TableCell>
                    )}
                    {columnVisibility.taxAmount && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(taxAmount)}</TableCell>
                    )}
                    {columnVisibility.invoiceValue && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(invoice.totalAmount)}</TableCell>
                    )}
                    {columnVisibility.paymentReceived && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(paymentReceived)}</TableCell>
                    )}
                    {columnVisibility.balanceAmount && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {paymentStatusLower === 'paid' ? (
                          <Typography sx={{ whiteSpace: 'nowrap' }} color="success.main">₹0.00</Typography>
                        ) : paymentStatusLower === 'draft' || paymentStatusLower === 'deleted' ? (
                          <Typography sx={{ whiteSpace: 'nowrap' }} color="text.secondary">N/A</Typography>
                        ) : (
                          <Typography sx={{ whiteSpace: 'nowrap' }} color={paymentStatusLower === 'overdue' ? 'error.main' : 'warning.main'}>
                            {formatCurrency(balanceDue)}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.actions && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex' }}>
                          <IconButton size="small" onClick={() => handleViewInvoice(invoice)} title="View Invoice">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {displayedInvoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      No invoices found matching your criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination component */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
          component="div"
          count={filteredInvoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ mt: 2 }}
        />
      </>
    );
  };

  const getPaymentStatusLabel = (status: string): string => {
    const normalizedStatus = (status || '').toLowerCase();
    switch (normalizedStatus) {
      case 'paid':
        return 'Payment Received';
      case 'pending':
        return 'Payment Pending';
      case 'overdue':
        return 'Payment Overdue';
      case 'draft':
        return 'Draft';
      case 'deleted':
        return 'Deleted';
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    }
  };

  const getPaymentStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    const normalizedStatus = (status || '').toLowerCase();
    switch (normalizedStatus) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'draft':
        return 'info';
      case 'deleted':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Sales Overview
          </Typography>

          <Box display="flex" alignItems="center" sx={{ flexGrow: 1, mx: 3 }}>
            <FormControl variant="outlined" size="small" sx={{ width: '300px', ml: 3 }}>
              <OutlinedInput
                placeholder="Search Invoice Number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                }
              />
            </FormControl>
          </Box>

          <Box>
            <Tooltip title={showCards ? "Hide Summary Cards" : "Show Summary Cards"}>
              <IconButton onClick={() => setShowCards(!showCards)} color="primary">
                {showCards ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
              <IconButton onClick={() => setShowFilters(!showFilters)} color="primary">
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Data">
              <IconButton onClick={() => setExportDialogOpen(true)} color="primary">
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Table in Full Page">
              <IconButton onClick={() => setFullPageTableOpen(true)} color="primary">
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchInvoices} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {renderSummaryCards()}
        {renderFilters()}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          renderTable()
        )}

        <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
          <DialogTitle>Export Data</DialogTitle>
          <DialogContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportAllData}
                  onChange={(e) => setExportAllData(e.target.checked)}
                />
              }
              label="Export all data (unfiltered)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
            <Button onClick={exportToCSV} variant="contained" color="primary">
              Export
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>

        {/* Invoice View Dialog */}
        <Dialog
          open={viewInvoiceDialogOpen}
          onClose={handleCloseViewInvoice}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Invoice #{selectedInvoice?.invoiceNumber}</Typography>
              <IconButton
                aria-label="close"
                onClick={handleCloseViewInvoice}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            {selectedInvoice && (
              <Box sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" fontWeight="500">Client</Typography>
                    <Typography variant="body2">{selectedInvoice.clientName}</Typography>
                    <Typography variant="body2">{selectedInvoice.clientDetails?.address || 'Address not available'}</Typography>
                    {selectedInvoice.clientDetails?.gstin && (
                      <Typography variant="body2">GSTIN: {formatGSTN(selectedInvoice.clientDetails.gstin)}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" fontWeight="500">Invoice Details</Typography>
                      <Typography variant="body2">Date: {formatDate(new Date(selectedInvoice.date))}</Typography>
                      <Typography variant="body2">
                        Status:
                        <Chip
                          label={getPaymentStatusLabel(selectedInvoice.paymentStatus)}
                          color={getPaymentStatusColor(selectedInvoice.paymentStatus)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ mb: 1 }}>Items</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>HSN</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell align="right">GST</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoice.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.productName}</TableCell>
                          <TableCell>{item.hsn}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">₹{item.rate.toFixed(2)}</TableCell>
                          <TableCell align="right">{item.gstRate}%</TableCell>
                          <TableCell align="right">₹{item.amount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Box sx={{ width: '250px', textAlign: 'right' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2">Total:</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">₹{selectedInvoice.totalAmount.toFixed(2)}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseViewInvoice} variant="outlined" sx={{ borderRadius: 1 }}>
              Close
            </Button>
            <Button
              startIcon={<PrintIcon />}
              variant="outlined"
              sx={{ borderRadius: 1 }}
              onClick={handlePrintInvoice}
            >
              Print
            </Button>
            <Button
              startIcon={<PrintIcon />}
              variant="outlined"
              sx={{ borderRadius: 1 }}
              onClick={handleThermalPrint}
            >
              Thermal Print
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              sx={{ borderRadius: 1 }}
              onClick={handleSavePDF}
            >
              Save PDF
            </Button>
            <Button
              startIcon={<EmailIcon />}
              variant="outlined"
              sx={{ borderRadius: 1 }}
              onClick={handleEmailPDF}
            >
              Email PDF
            </Button>
            <Button
              startIcon={<ArrowForwardIcon />}
              variant="outlined"
              color="primary"
              onClick={handleViewNextInvoice}
              sx={{ borderRadius: 1 }}
            >
              View Next
            </Button>
            <Button
              startIcon={<EditIcon />}
              variant="contained"
              color="primary"
              onClick={() => {
                handleCloseViewInvoice();
                if (selectedInvoice) {
                  handleEditInvoice(selectedInvoice.id);
                }
              }}
              sx={{
                borderRadius: 1,
                backgroundImage: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
              }}
            >
              Edit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Full Page Table Dialog */}
        <Dialog
          open={fullPageTableOpen}
          onClose={() => setFullPageTableOpen(false)}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              width: '95vw',
              height: '95vh',
              maxWidth: 'none',
              maxHeight: 'none',
              margin: 0,
              borderRadius: 2
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            color: 'white',
            fontWeight: 'bold'
          }}>
            Sales Data - Full Page View
            <IconButton
              onClick={() => setFullPageTableOpen(false)}
              sx={{ color: 'white' }}
            >
              <FullscreenExitIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: 'calc(100% - 64px)', overflow: 'hidden' }}>
            {renderTable(true)}
          </DialogContent>
        </Dialog>

        {/* Context Menu for Column Management */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={() => contextMenu && toggleColumnVisibility(contextMenu.column)}>
            <ListItemIcon>
              <VisibilityOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {contextMenu && columnVisibility[contextMenu.column as keyof typeof columnVisibility]
                ? 'Hide Column'
                : 'Show Column'
              }
            </ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </AnimatedPage>
  );
};

export default Sales; 
